import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBook, FaMusic, FaArrowRight } from 'react-icons/fa';
import { FaHandsPraying } from 'react-icons/fa6';
import { GiByzantinTemple } from 'react-icons/gi';
import Footer from '../component/common/Footer';
import LibraryNavbar from '../component/core/LibrarySection/LibraryNavbar';
import Breadcrumb from '../component/common/Breadcrumb';
import MusicCard from '../component/core/MusicSection/MusicCard';
import LyricsModal from '../component/core/MusicSection/LyricsModal';
import { getAllMusic, getMusicById } from '../Services/musicService';
import { useLanguage } from '../i18n/LanguageContext';

const Songs = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [musicData, setMusicData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [showLyricsModal, setShowLyricsModal] = useState(false);
  const [lyrics, setLyrics] = useState(null);

  // Music categories configuration
  const categories = [
    {
      title: "Aarti",
      translatedTitle: t('arti'),
      description: t('artiDescription'),
      icon: FaHandsPraying,
      color: "from-orange-500 to-red-500",
      type: "aarti"
    },
    {
      title: "Chalisa",
      translatedTitle: t('chalisa'),
      description: t('chalisaDescription'),
      icon: FaBook,
      color: "from-blue-500 to-purple-500",
      type: "chalisa"
    },
    {
      title: "Mantra",
      translatedTitle: t('mantra'),
      description: t('mantraDescription'),
      icon: GiByzantinTemple,
      color: "from-green-500 to-teal-500",
      type: "mantra"
    },
    {
      title: "Meditation",
      translatedTitle: t('meditation'),
      description: t('meditationDescription'),
      icon: FaMusic,
      color: "from-purple-500 to-pink-500",
      type: "meditation"
    }
  ];

  useEffect(() => {
    fetchMusicData();
  }, []);

  const fetchMusicData = async () => {
    try {
      setLoading(true);
      const response = await getAllMusic();
      if (response.status === "success") {
        setMusicData(response.data);
      } else {
        setError("Failed to fetch music data");
      }
    } catch (err) {
      setError("Error loading music data");
      console.error("Error fetching music:", err);
    } finally {
      setLoading(false);
    }
  };

  const getMusicByType = (type) => {
    return musicData.filter(music => music.type.toLowerCase() === type.toLowerCase());
  };

  // Audio handling is now managed by AudioContext

  const handleShowLyrics = async (music) => {
    try {
      setSelectedMusic(music);
      setShowLyricsModal(true);
      const response = await getMusicById(music.id);
      if (response.status === "success") {
        setLyrics(response.data);
      }
    } catch (err) {
      console.error("Error fetching lyrics:", err);
    }
  };

  const handleCloseLyrics = () => {
    setShowLyricsModal(false);
    setSelectedMusic(null);
    setLyrics(null);
  };

  const handleViewMore = (categoryTitle) => {
    navigate(`/songs/${categoryTitle}`);
  };

  if (loading) {
    return (
      <>
        <LibraryNavbar sectionTitle="" subsectionName="" />
        <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('loadingMusic')}</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <LibraryNavbar sectionTitle="" subsectionName="" />
        <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pt-20 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={fetchMusicData}
              className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors"
            >
{t('tryAgain')}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LibraryNavbar sectionTitle="" subsectionName="" />
      
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pt-20">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Breadcrumb */}
          <Breadcrumb 
            customItems={[
              { label: 'Home', path: '/', icon: <span>🏠</span> },
              { label: 'Songs', path: '/songs', isLast: true }
            ]}
          />

          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-orange-800 mb-4">
              🎵 {t('divineSongsPrayers')}
            </h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              {t('exploreCollection')}
            </p>
          </div>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {categories.map((category, index) => {
              const IconComponent = category.icon;
              const categoryMusic = getMusicByType(category.type);
              const displayMusic = categoryMusic.slice(0, 3); // Show first 3 items
              
              return (
                <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                  {/* Category Header */}
                  <div className={`bg-gradient-to-r ${category.color} p-6 text-white`}>
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-white bg-opacity-20 rounded-xl">
                        <IconComponent className="text-3xl" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold">{category.translatedTitle}</h2>
                        <p className="text-white text-opacity-90">{category.description}</p>
                        <p className="text-white text-opacity-75 text-sm">
                          {categoryMusic.length} {categoryMusic.length === 1 ? t('song') : t('songsAvailable')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Music List */}
                  <div className="p-6">
                    {displayMusic.length > 0 ? (
                      <div className="space-y-3 mb-6">
                        {displayMusic.map((music, itemIndex) => (
                          <MusicCard
                            key={music.id}
                            music={music}
                            onShowLyrics={handleShowLyrics}
                          />
                      ))}
                    </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p>{t('noMusicAvailable').replace('{type}', category.translatedTitle.toLowerCase())}</p>
                      </div>
                    )}

                    {/* View More Button */}
                    {categoryMusic.length > 3 && (
                    <button
                      onClick={() => handleViewMore(category.title)}
                      className={`w-full bg-gradient-to-r ${category.color} text-white py-3 px-6 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 group`}
                    >
{t('viewMore')} {category.translatedTitle}
                      <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                    </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Additional Info Section */}
          <div className="mt-16 text-center">
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                🌟 {t('moreContentComingSoon')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('continuouslyAdding')}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-2 text-orange-600">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                  <span className="font-medium">{t('newContentAddedWeekly')}</span>
                </div>
                <div className="flex items-center gap-2 text-orange-600">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                  <span className="font-medium">{t('highQualityAudio')}</span>
                </div>
                <div className="flex items-center gap-2 text-orange-600">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                  <span className="font-medium">{t('traditionalModernVersions')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lyrics Modal */}
      <LyricsModal
        isOpen={showLyricsModal}
        onClose={handleCloseLyrics}
        music={selectedMusic}
        lyrics={lyrics}
      />

      <Footer />
    </>
  );
};

export default Songs;
